import React from 'react';

import moment from 'moment';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import FormContainer from '../../../FormContainer';
import Table from '../../../../Table';
import Pagination from '../../../../Pagination';
import AddButton from '../../../../AddButton';
import AddOrEditPageTabs from '../../../AddOrEditPageTabs';

import useNewBillForm from '../../../../../forms/loan/newBill';
import BillService from '../../../../../services/BillService';
import LoanService from '../../../../../services/LoanService';
import { useUiContext } from '../../../../../context/UiContext';
import renderLinksList from '../../../../../helpers/tables/renderLinksList';
import usePagination from '../../../../../hooks/usePagination';
import useQueryParam from '../../../../../hooks/useQueryParam';
import getBillBadgeColor from '../../../../../helpers/tables/getBillBadgeColor';

const tabs = [
  {
    label: 'Bills',
  },
  {
    label: 'Bill Payments',
  },
];

export default function Bills({ loanId, isFetching, loan, isLender }) {
  const { showToast } = useUiContext();

  const navigate = useNavigate();

  const { data: bills, refetch } = useQuery({
    queryKey: ['loanBills', loanId],
    queryFn: () => LoanService.getLoanBills({ loanId }),
    enabled: !!loanId && !isLender,
  });

  const {
    currentPage,
    switchToPage,
    pageTotal,
    isFetching: areLenderBillsFetching,
    data,
  } = usePagination({
    queryKey: ['loanBills', loanId],
    queryFn: LoanService.getLoanBills,
    queryFnArgs: { loanId },
  });

  const { getQueryParam, addQueryParam } = useQueryParam('addNew');
  const isAddNew = getQueryParam();

  const { mutate: createBill, isPending } = useMutation({
    mutationFn: (formValues) => {
      // eslint-disable-next-line no-param-reassign
      delete formValues._servicer;

      return BillService.createBill({
        formValues: {
          ...formValues,
          borrower_id:
            formValues.payee_type === 'borrower'
              ? formValues.borrower_id
              : null,
          vendor_id:
            formValues.payee_type === 'vendor' ? formValues.vendor_id : null,
          lender_id:
            formValues.payee_type === 'lender' ? formValues.lender_id : null,
        },
        loanId,
        type: formValues.type,
      });
    },
    onSuccess: (bill) => {
      showToast({
        text: 'Bill created successfully',
        type: 'success',
      });

      refetch();
      navigate(`/loans/${loanId}/bills/${bill.id}?tab=Bill`);
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const newBillForm = useNewBillForm(
    createBill,
    null,
    loanId,
    true,
    loan?.borrowers,
    loan?._property_of_loan?.id
  );

  const createBillLink = (row) => `/loans/${loanId}/bills/${row.id}?tab=Bill`;

  const cols = [
    {
      title: 'Number',
      keys: ['pixel_bill_number'],
      link: createBillLink,
    },
    {
      title: 'Due date',
      keys: ['due_date'],
      link: createBillLink,
      format: (value) => moment(value).format('M/D/YY'),
    },
    {
      title: 'Pay to',
      keys: ['unknown'],
      format: (_, row) => {
        if (row.payee_type === 'servicer') {
          return loan?._servicer?.name;
        }

        if (row.payee_type === 'vendor') {
          return row?._vendor?.name;
        }

        if (row.payee_type === 'lender') {
          return row?._lender?.name;
        }

        if (row.payee_type === 'borrower') {
          const primaryBorrower = loan?.borrowers?.find(
            (borrower) => borrower.position === 'primary'
          );

          if (!primaryBorrower) {
            return '';
          }

          return primaryBorrower?._borrower?.TIN_type === 'EIN'
            ? primaryBorrower?._borrower?.entity_name
            : `${primaryBorrower?._borrower?.first_name || ''} ${
                primaryBorrower?._borrower?.last_name || ''
              }`;
        }

        return '';
      },
    },
    {
      title: 'Type',
      keys: ['type'],
    },
    {
      title: 'Amount',
      keys: ['_amount.total'],
      type: 'currency',
    },
    {
      title: 'Status',
      keys: ['status'],
      link: createBillLink,
      type: 'badge',
      badgeColor: getBillBadgeColor,
    },
    {
      title: 'Distributed',
      keys: ['_distributed.total'],
      type: 'currency',
    },
    {
      title: 'Bill Payments',
      keys: ['_distributed.total'],
      format: (value, row) => {
        return renderLinksList({
          data: row._bill_payments,
          createLabel: (item) => item.pixel_number,
          createLink: (item) =>
            `/loans/${row.loan_id}/bill-payments/${item.id}?tab=Bill Payment`,
        });
      },
    },
  ];

  return (
    <div>
      <div className="mb-4 flex justify-between md:items-center flex-col md:flex-row gap-2">
        <AddOrEditPageTabs tabs={tabs} />
        <AddButton
          style={{
            visibility: isAddNew || isLender ? 'hidden' : 'visible',
          }}
          onClick={addQueryParam}
        >
          Create Bill or Distribution
        </AddButton>
      </div>
      <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
        {!isLender && isAddNew && (
          <div className="mb-10">
            <FormContainer
              form={newBillForm}
              tabindex="0"
              isSubmitting={isPending}
              noBillComId
            />
          </div>
        )}
        {!isAddNew && (
          <>
            <Table
              cols={cols}
              rows={isLender ? data?.items : bills}
              title="Bills"
              isFetching={isLender ? areLenderBillsFetching : isFetching}
              minHeight={565}
            />
            {isLender && (
              <Pagination
                pageTotal={pageTotal}
                switchToPage={switchToPage}
                currentPage={currentPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
